import { useState } from "react";
import { Share2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PLACEHOLDERS = [
  "Audience — e.g. Demo for Greenport assessors · 14 Feb",
  "Use-case cue — e.g. Storm + yard congestion + pharma cut-off",
  "One feedback question — e.g. Which receipt would your auditor want first?",
];

const EXPIRY = [
  { value: "7",    label: "Expires in 7 days",  days: 7 },
  { value: "30",   label: "Expires in 30 days", days: 30 },
  { value: "none", label: "No expiry",          days: null },
];

const MAX_LEN = 120;

export default function ShareSnapshot({ enabledSifty = true, enabledRWI = false }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lines, setLines] = useState(["", "", ""]);
  const [expiry, setExpiry] = useState("7");
  const [lastUrl, setLastUrl] = useState(null);

  const setLine = (i, v) =>
    setLines(prev => prev.map((x, idx) => idx === i ? v.replace(/[\r\n\t]+/g, " ").slice(0, MAX_LEN) : x));

  const reset = () => { setLines(["", "", ""]); setExpiry("7"); };

  const submit = async () => {
    setBusy(true);
    try {
      const days = EXPIRY.find(e => e.value === expiry)?.days ?? null;
      const note_lines = lines.map(l => l.trim()).filter(Boolean).slice(0, 3);
      const res = await api.createSnapshot({
        enabled_sifty: enabledSifty,
        enabled_rwi: enabledRWI,
        expiry_days: days,
        note_lines,
      });
      const url = `${window.location.origin}/snapshot/${res.token}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      setLastUrl(url);
      setOpen(false);
      reset();
      toast.success("Assessor link copied — read-only snapshot ready to share", {
        description: days ? `Expires in ${days} days` : "No expiry",
        action: { label: "Open", onClick: () => window.open(url, "_blank") },
      });
    } catch (e) {
      toast.error("Could not create snapshot", { description: String(e?.message || e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" className="rounded-sm h-9" data-testid="copy-assessor-link-btn">
            <Share2 size={13} className="mr-2"/> Copy assessor link
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[520px] rounded-sm" data-testid="snapshot-dialog">
          <DialogHeader>
            <DialogTitle className="display text-[20px]">Create assessor snapshot</DialogTitle>
            <DialogDescription className="text-[13px]">
              The current scenario, receipts and enabled panels will be frozen into a read-only link.
              Add an optional host note (up to 3 short lines) to frame the review.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-1">
            <div>
              <Label className="fc-eyebrow mb-2 block">Host note · optional</Label>
              <div className="space-y-2">
                {[0, 1, 2].map(i => (
                  <div key={i} className="space-y-1">
                    <Input
                      value={lines[i]}
                      onChange={(e) => setLine(i, e.target.value)}
                      placeholder={PLACEHOLDERS[i]}
                      maxLength={MAX_LEN}
                      data-testid={`note-line-${i + 1}`}
                      className="rounded-sm h-9 text-[13px]"
                    />
                    <div className="flex justify-between text-[10px] text-[var(--ink-soft)] mono">
                      <span>Line {i + 1}</span>
                      <span>{lines[i].length}/{MAX_LEN}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[var(--ink-soft)] mt-2">
                Plain text only. The note is frozen on creation and cannot be edited later.
              </p>
            </div>

            <div>
              <Label className="fc-eyebrow mb-2 block">Snapshot expiry</Label>
              <RadioGroup value={expiry} onValueChange={setExpiry} className="grid grid-cols-3 gap-2">
                {EXPIRY.map(e => (
                  <label
                    key={e.value}
                    htmlFor={`exp-${e.value}`}
                    className={`flex items-center gap-2 border p-2.5 cursor-pointer text-[12px] rounded-sm ${
                      expiry === e.value ? "border-[var(--navy)] bg-[#F4F7FB]" : "border-[var(--line)] hover:bg-[var(--paper)]"
                    }`}
                    data-testid={`expiry-${e.value}`}
                  >
                    <RadioGroupItem id={`exp-${e.value}`} value={e.value} className="shrink-0"/>
                    {e.label}
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>

          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm h-9" data-testid="snapshot-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="bg-[var(--navy)] hover:bg-black rounded-sm h-9"
              data-testid="snapshot-create-btn"
            >
              {busy ? "Creating…" : "Create & copy link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {lastUrl && (
        <a
          href={lastUrl}
          target="_blank" rel="noreferrer"
          className="text-[12px] text-[var(--navy)] inline-flex items-center gap-1 fc-link-underline px-2"
          data-testid="open-last-snapshot"
        >
          Open <ExternalLink size={11}/>
        </a>
      )}
    </div>
  );
}
